import {HttpErrorResponse} from '@angular/common/http';
import {type Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {firstValueFrom} from 'rxjs';
import {SmConfirmationDialogService} from '../shared/components/sm-confirmation-dialog/sm-confirmation-dialog.service';
import type {SmContextMenuItemModel} from '../shared/components/sm-context-menu-item/sm-context-menu-item.component';
import type {AppLanguageCode} from '../shared/i18n/app-language';
import {navigateItemAction} from './item-action-navigation';
import {ItemsService} from './items.service';

export type ItemActionPathSegment = {
  id: string;
  name: string;
};

export type ItemActionTarget = {
  canEdit?: boolean;
  id: string;
  name: string;
  description: string | null;
  imageUrl: string | null;
  quantity: number;
};

export type ItemActionNavigationContext = {
  containerId: string;
  currentPath: ItemActionPathSegment[];
  containerName?: string;
  languageCode: AppLanguageCode;
  sourceOrigin?: 'item-details';
  returnUrl: string;
};

export type ItemActionMenuContext = ItemActionNavigationContext & {
  afterDelete?: () => void | Promise<void>;
  closeMenu?: () => void;
};

type ItemActionMenuDependencies = {
  confirmationDialogService: SmConfirmationDialogService;
  itemsService: ItemsService;
  translateService: TranslateService;
};

const ITEM_DELETE_DIALOG_KEYS = {
  title: 'containers.page.itemDeleteDialogTitle',
  cancelLabel: 'common.actions.cancel',
  closeLabel: 'containers.page.itemDeleteDialogCloseLabel',
  confirmLabel: 'containers.page.itemDeleteDialogDeleteLabel',
  questionPrefix: 'containers.page.itemDeleteDialogQuestionPrefix',
  questionSuffix: 'containers.page.itemDeleteDialogQuestionSuffix',
  warning: 'containers.page.itemDeleteDialogWarning',
  error: 'containers.page.itemDeleteError',
  errorNotFound: 'containers.page.itemDeleteErrors.notFound',
  errorFallback: 'containers.page.itemDeleteErrors.fallback',
} as const;

export async function handleItemMenuItemSelect(
  router: Router,
  menuItem: SmContextMenuItemModel,
  context: ItemActionMenuContext,
  item: ItemActionTarget,
  dependencies: ItemActionMenuDependencies,
): Promise<void> {
  context.closeMenu?.();

  if (menuItem.id === 'add-to-basket') {
    navigateItemAction(router, menuItem.id, context, item);
    return;
  }

  if (item.canEdit === false) {
    return;
  }

  if (menuItem.id === 'delete') {
    await openDeleteConfirmation(context, item, dependencies);
    return;
  }

  navigateItemAction(router, menuItem.id, context, item);
}

function buildItemDeleteErrorMessage(translateService: TranslateService, error: unknown): string {
  const getStringValue = (value: unknown): string => {
    if (typeof value === 'string') {
      return value.trim();
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }

    if (Array.isArray(value)) {
      return value
        .map((entry) => getStringValue(entry))
        .filter((entry) => entry.length > 0)
        .join(' ')
        .trim();
    }

    if (value && typeof value === 'object') {
      const nestedMessage = getStringValue((value as {message?: unknown}).message);
      if (nestedMessage.length > 0) {
        return nestedMessage;
      }

      const nestedError = getStringValue((value as {error?: unknown}).error);
      if (nestedError.length > 0) {
        return nestedError;
      }
    }

    return '';
  };

  const isTransportErrorMessage = (value: string): boolean => value.toLowerCase().startsWith('http failure response');

  if (error instanceof HttpErrorResponse) {
    if (error.status === 404) {
      return translateService.instant(ITEM_DELETE_DIALOG_KEYS.errorNotFound);
    }

    const payload = error.error as {message?: unknown; error?: unknown} | string | null | undefined;
    const payloadMessage = getStringValue(payload);

    if (payloadMessage.length > 0) {
      return payloadMessage;
    }

    const requestMessage = getStringValue(error.message);
    if (requestMessage.length > 0 && !isTransportErrorMessage(requestMessage)) {
      return requestMessage;
    }
  }

  if (error instanceof Error) {
    const message = getStringValue(error.message);
    if (message.length > 0) {
      return message;
    }
  }

  return translateService.instant(ITEM_DELETE_DIALOG_KEYS.errorFallback);
}

async function openDeleteConfirmation(
  context: ItemActionMenuContext,
  item: ItemActionTarget,
  dependencies: ItemActionMenuDependencies,
): Promise<void> {
  await dependencies.confirmationDialogService.open({
    title: dependencies.translateService.instant(ITEM_DELETE_DIALOG_KEYS.title),
    cancelLabel: dependencies.translateService.instant(ITEM_DELETE_DIALOG_KEYS.cancelLabel),
    confirmLabel: dependencies.translateService.instant(ITEM_DELETE_DIALOG_KEYS.confirmLabel),
    closeLabel: dependencies.translateService.instant(ITEM_DELETE_DIALOG_KEYS.closeLabel),
    confirmTone: 'danger',
    contentAlign: 'center',
    compactActions: true,
    heroIconName: 'trash-outline',
    showCloseButton: false,
    bodyBlocks: [
      {
        segments: [
          {text: dependencies.translateService.instant(ITEM_DELETE_DIALOG_KEYS.questionPrefix)},
          {text: ` ${item.name}`, emphasis: true},
          {text: dependencies.translateService.instant(ITEM_DELETE_DIALOG_KEYS.questionSuffix)},
        ],
      },
      {
        segments: [
          {text: dependencies.translateService.instant(ITEM_DELETE_DIALOG_KEYS.warning)},
        ],
        tone: 'danger',
      },
    ],
    onConfirm: async () => {
      await firstValueFrom(dependencies.itemsService.deleteItem(context.containerId, item.id));
      await context.afterDelete?.();
    },
    errorFormatter: (error) => buildItemDeleteErrorMessage(dependencies.translateService, error),
    errorFallback: dependencies.translateService.instant(ITEM_DELETE_DIALOG_KEYS.error),
  });
}
